import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { AddAreaSheet } from "./AddAreaSheet";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { deleteArea, getAreaContent } from "@/sideEffects/api";
import { selectActiveFirm } from "@/store/appSelectors";
import { useSelector } from "react-redux";
import { showApiError } from "@/lib/toast";
import { LoadingSmall } from "@/components/LoadingSmall";
import { useReactFlow, type Node } from "@xyflow/react";
import { UpdateAreaSheet } from "./UpdateAreaSheet";
import { NodeDataArea } from "./AreaPageTypes";

export interface MenuProps {
  id: string;
  top: number;
  left: number;
}

export const AreaContextMenu: React.FC<MenuProps> = ({ id, top, left }) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const { getNode } = useReactFlow();

  const node = getNode(id) as Node<NodeDataArea>;
  const queryClient = useQueryClient();
  const deleteAreaMutation = useMutation({
    mutationFn: () => {
      return deleteArea(String(activeFirm?.firmGuid), node?.data?.idString as string);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["area"] });
    },
    onError: showApiError,
  });

  const [showAddArea, setShowAddArea] = useState(false);
  const [showUpdateArea, setShowUpdateArea] = useState(false);

  const { data: areaContentData, isLoading: isAreaContentLoading } = useQuery({
    queryFn: () => getAreaContent(node?.data?.idString as string, String(activeFirm?.firmGuid)),
    queryKey: ["getAreaContent", node?.data?.idString],
    refetchOnMount: true,
  });

  const contentList = areaContentData?.data?.result || [];

  if (isAreaContentLoading) {
    return <LoadingSmall />;
  }

  return (
    <>
      <div
        style={{ top, left }}
        className="fixed min-w-[8rem] rounded-md border bg-popover p-1 text-popover-foreground shadow-md outline-none animate-in fade-in-0 z-50"
      >
        <div className="px-2 py-1.5">
          <p className="text-xs text-muted-foreground">{t("common.menu")}</p>
        </div>
        <div className="h-px bg-muted" />
        <button
          onClick={() => {
            setShowUpdateArea(true);
          }}
          className="relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
        >
          {t("common.entityDetail", { name: node?.data?.name })}
        </button>
        <button
          onClick={() => {
            setShowAddArea(true);
          }}
          className="relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
        >
          {t("common.addNewEntity", { entity: t("entities.area") })}
        </button>
        <button
          onClick={() => deleteAreaMutation.mutate()}
          className="relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground text-destructive"
        >
          {t("common.deleteEntity.buttonText")} {deleteAreaMutation.isPending && <LoadingSmall />}
        </button>
      </div>
      {showAddArea && (
        <AddAreaSheet
          onSuccess={() => setShowAddArea(false)}
          onOpenChange={() => setShowAddArea(false)}
          motherAreaintId={Number(node.data.intId)}
        />
      )}
      {showUpdateArea && (
        <UpdateAreaSheet
          onSuccess={() => setShowUpdateArea(false)}
          onOpenChange={() => setShowUpdateArea(false)}
          idString={node?.data?.idString}
          idNumber={node?.data?.intId}
          motherAreaintId={node?.data?.motherAreaintId}
          defaultValues={{
            name: node?.data?.name,
            areaTypeId: node?.data?.areaTypeId,
            timezone: node?.data?.timezone,
            defaultContentId: node?.data?.defaultContentintId,
            overwriteSub: node?.data?.overwriteSub,
            contentList: contentList.map((content) => ({
              contentId: content.contentintId,
              contentGroupId: content.contentGroupintId,
              id: content.id,
            })),
          }}
        />
      )}
    </>
  );
};
